import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data);

    // Handle token expiration or invalid token
    if (error.response?.status === 401 || error.response?.status === 422) {
      const errorMsg = error.response?.data?.msg || error.response?.data?.error;
      // Check for JWT errors or token-related issues
      if (errorMsg && (
        errorMsg.includes('token') ||
        errorMsg.includes('expired') ||
        errorMsg.includes('Invalid') ||
        errorMsg.includes('Subject must be a string') ||
        errorMsg.includes('Signature') ||
        errorMsg.includes('decode')
      )) {
        console.log('Token issue detected, clearing token and redirecting to login');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (email, password, name, invitationToken) =>
    api.post('/auth/register', { email, password, name, invitation_token: invitationToken }),

  login: (email, password) =>
    api.post('/auth/login', { email, password }),

  getCurrentUser: () =>
    api.get('/auth/me'),
};

// Invitation API
export const invitationAPI = {
  validate: (token) =>
    axios.get(`${API_URL}/invitations/validate/${token}`),

  accept: (token) =>
    api.post(`/invitations/accept/${token}`),

  getGroupInvitations: (groupId) =>
    api.get(`/groups/${groupId}/invitations`),
};

// Waitlist API
export const waitlistAPI = {
  join: (email, name, message) =>
    axios.post(`${API_URL}/waitlist`, { email, name, message }),

  getWaitlist: () =>
    api.get('/waitlist'),

  inviteFromWaitlist: (waitlistId, groupId) =>
    api.post(
      `/waitlist/${waitlistId}/invite`,
      groupId ? { group_id: groupId } : {}
    ),
};

export const webhookAPI = {
  forwardEmail: (payload, secret) =>
    axios.post(`${API_URL}/webhook/email-received`, payload, {
      headers: secret ? { 'X-Webhook-Secret': secret } : {}
    }),
};

// Groups API
export const groupsAPI = {
  createGroup: (name, description, organizer_opts_out) =>
    api.post('/groups', { name, description, organizer_opts_out }),

  getUserGroups: () =>
    api.get('/groups'),

  getGroup: (groupId) =>
    api.get(`/groups/${groupId}`),

  updateGroup: (groupId, data) =>
    api.put(`/groups/${groupId}`, data),

  deleteGroup: (groupId) =>
    api.delete(`/groups/${groupId}`),

  getGroupPreview: (inviteCode) =>
    axios.get(`${API_URL}/groups/preview/${inviteCode}`),

  joinGroup: (inviteCode) =>
    api.post(`/groups/join/${inviteCode}`),

  inviteToGroup: (groupId, email, name) =>
    api.post(`/groups/${groupId}/invite`, { email, name }),

  removeMember: (groupId, memberId) =>
    api.delete(`/groups/${groupId}/members/${memberId}`),

  generateRiddle: (groupId) =>
    api.post(`/groups/${groupId}/riddle`),

  createAssignments: (groupId) =>
    api.post(`/groups/${groupId}/assign`),

  getMyAssignment: (groupId) =>
    api.get(`/groups/${groupId}/my-assignment`),

  getRestrictions: (groupId) =>
    api.get(`/groups/${groupId}/restrictions`),

  addRestriction: (groupId, userId, restrictedUserId) =>
    api.post(`/groups/${groupId}/restrictions`, { user_id: userId, restricted_user_id: restrictedUserId }),

  removeRestriction: (groupId, restrictionId) =>
    api.delete(`/groups/${groupId}/restrictions/${restrictionId}`),
};

// Wish List API
export const wishlistAPI = {
  getMyWishlist: () =>
    api.get('/wishlist'),

  addItem: (name, url) =>
    api.post('/wishlist', { name, url }),

  updateItem: (itemId, name, url) =>
    api.put(`/wishlist/${itemId}`, { name, url }),

  deleteItem: (itemId) =>
    api.delete(`/wishlist/${itemId}`),

  getUserWishlist: (userId) =>
    api.get(`/users/${userId}/wishlist`),
};

export const referralAPI = {
  getMyInvites: () =>
    api.get('/referrals'),
};

export const notificationsAPI = {
  getNotifications: (limit = 20) =>
    api.get(`/notifications?limit=${limit}`),

  markAsRead: (notificationId) =>
    api.post(`/notifications/${notificationId}/read`),
};

export default api;
